import { IStudentCourse, IStudentLesson } from "@features/shared/entities/enrollments";
import { ILesson, LessonCompleteResponse } from "@features/shared/entities/lessons";
import { UserEnrollmentStatus } from "@features/shared/entities/status";
import { IStreakStats } from "@features/shared/entities/streak";

export type LessonCompletionRequest = {
    courseId: number;
    topicId: number;
    lessonId: number;
    isDraftCourse: boolean;
};

export type ActiveLessonCompletion = LessonCompletionRequest & {
    wasCompleted: boolean;
    streakBefore: IStreakStats | null;
};

export type LessonCompletionResult = {
    courseId: number;
    topicId: number;
    lessonId: number;
    isFirstCompletion: boolean;
    isTopicCompleted: boolean;
    isCourseCompleted: boolean;
    isStreakExtended: boolean;
    /** Course / topic progress (0-100) reported with the completion, when the backend sends it. */
    courseProgress: number | null;
    topicProgress: number | null;
};

export function isStreakExtended(before: IStreakStats | null, after: IStreakStats | null): boolean {
    if (before === null || after === null) return false;
    return before.currentStreak !== after.currentStreak || before.id !== after.id;
}

export function hasCompletionDetails(response: LessonCompleteResponse): boolean {
    return typeof response.firstCompletion === "boolean";
}

export function repeatedCompletionResult(completion: ActiveLessonCompletion): LessonCompletionResult {
    return {
        courseId: completion.courseId,
        topicId: completion.topicId,
        lessonId: completion.lessonId,
        isFirstCompletion: false,
        isTopicCompleted: false,
        isCourseCompleted: false,
        isStreakExtended: false,
        courseProgress: null,
        topicProgress: null
    };
}

export function completionResultFromResponse(
    completion: ActiveLessonCompletion,
    response: LessonCompleteResponse
): LessonCompletionResult {
    const isFirstCompletion = response.firstCompletion === true;

    return {
        courseId: completion.courseId,
        topicId: completion.topicId,
        lessonId: completion.lessonId,
        isFirstCompletion,
        isTopicCompleted: isFirstCompletion && Boolean(response.topic?.completed),
        isCourseCompleted: isFirstCompletion && !completion.isDraftCourse && Boolean(response.course?.completed),
        isStreakExtended: isStreakExtended(completion.streakBefore, response.streak ?? null),
        courseProgress: typeof response.course?.progress === "number" ? response.course.progress : null,
        topicProgress: typeof response.topic?.progress === "number" ? response.topic.progress : null
    };
}

export function completionResultFromEnrollments(
    completion: ActiveLessonCompletion,
    {
        enrollmentsCourses,
        enrollmentsLessons,
        lessons,
        streakStats
    }: {
        enrollmentsCourses: IStudentCourse[];
        enrollmentsLessons: IStudentLesson[];
        lessons: ILesson[];
        streakStats: IStreakStats | null;
    }
): LessonCompletionResult {
    const completedLessonIds = new Set(
        enrollmentsLessons
            .filter((enrollment) => enrollment.status === UserEnrollmentStatus.Completed)
            .map((enrollment) => enrollment.lesson_id)
    );
    const topicLessons = lessons.filter((lesson) => lesson.topic_id === completion.topicId);
    const courseEnrollment = enrollmentsCourses.find((enrollment) => enrollment.course_id === completion.courseId);

    return {
        courseId: completion.courseId,
        topicId: completion.topicId,
        lessonId: completion.lessonId,
        isFirstCompletion: true,
        isTopicCompleted: topicLessons.length > 0 && topicLessons.every((lesson) => completedLessonIds.has(lesson.id)),
        isCourseCompleted:
            !completion.isDraftCourse && courseEnrollment?.status === UserEnrollmentStatus.Completed,
        isStreakExtended: isStreakExtended(completion.streakBefore, streakStats),
        courseProgress: typeof courseEnrollment?.progress === "number" ? courseEnrollment.progress : null,
        topicProgress: null
    };
}
